import Button from 'components/Button';
import FeaturedIcon from 'components/FeaturedIcon';
import CheckIcon from 'components/icons/Check';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import { PageHeadContainer, PageTitle } from 'components/PageHead';
import Space from 'components/Space';
import { FC, useState } from 'react';
import { InlineWidget, useCalendlyEventListener } from 'react-calendly';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import MentorForm from 'types/MentorForm';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';
import { getLocaleDate, getLocaleTime } from 'utils/time';

const Container = styled.div`
  width: 100%;
  max-width: 45rem;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1.5rem;
  margin-bottom: 7rem;
  color: var(--gray-700);

  h2 {
    color: var(--gray-900);
    font-size: 1.5rem;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  p {
    margin-top: 0.5rem;
  }

  i {
    display: flex;
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: var(--gray-500);
  }

  button {
    margin-top: 2rem;
    margin-bottom: 2rem;
    margin-left: auto;
    margin-right: auto;
  }

  ul li {
    margin-left: 2rem;
    list-style-position: inside;
    list-style-type: disc;
  }
`;

const Head: FC = () => {
  return (
    <PageHeadContainer>
      <PageTitle>
        <h1>Formation mentor</h1>
      </PageTitle>
    </PageHeadContainer>
  );
};

const Training: FC = () => {
  const { data: user } = useSWR<User>('/auth/profile');
  const { data: form } = useSWR<MentorForm>('/calendly/training-info');

  const [loading, setLoading] = useState<boolean>(false);
  useCalendlyEventListener({
    onEventScheduled: (e) => {
      setLoading(true);
      privateAPI
        .post('/calendly/schedule-event', {
          eventUri: e.data.payload.event.uri,
          inviteeUri: e.data.payload.invitee.uri,
        })
        .finally(() => {
          setLoading(false);
          mutate('/auth/profile');
        });
    },
  });

  if (user?.mentorStatus === 'TRAINING') {
    return (
      <div>
        <Head />

        {!form || loading || !user ? (
          <LoaderContainer height="500px">
            <DefaultLoader size={1.5} />
          </LoaderContainer>
        ) : (
            <Container>
              <p>
                Afin que l'on puisse vous assigner un ou plusieurs mentorés, vous
                devez participer à une des formations que nous proposons.
            </p>
              <p>Vous avez le choix entre 2 types de formation:</p>
              <ul>
                <li>
                  la <b>formation standard</b>, d'une durée d'1h30, en
                visioconférence avec l'équipe
              </li>
                <li>
                  la <b>formation flash</b>, questionnaire de 10 minutes maximum
              </li>
              </ul>
              <p>
                Vous n'avez besoin de passer qu'une seule des deux formations pour
                être accepté en tant que mentor. Mais vous aurez toujours la
                possiblité de vous inscrire à la formation standard par la suite.
            </p>
              <Space height={2} />
              <p>
                Pour participer à la <b>formation flash</b>, cliquez sur le bouton
              ci-dessous:
            </p>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSfpBNX1QAXLYl8Bww1MuWx5SrgGig2ixtXawHCKgJv59gIo8Q/viewform"
                target="_blank"
                rel="noreferrer"
              >
                <Button type="button" color="primary">
                  Participer à la Formation Flash
              </Button>
              </a>
              <Space height={2} />
              <p>
                Pour participer à la <b>formation standard</b>, veuillez réserver
              votre créneau à l'aide du calendrier ci-dessous:
            </p>
              <Space height={1.5} />
              <InlineWidget
                url="https://calendly.com/mentorat-2/formation-mentors?hide_gdpr_banner=1"
                prefill={{
                  email: user.email,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  customAnswers: {
                    a1: user.phone || '',
                  },
                }}
              />
            </Container>
          )}
      </div>
    );
  }

  if (user?.mentorStatus === 'TRAINING_REGISTERED') {
    return (
      <div>
        <Head />

        {!form || loading || !user ? (
          <LoaderContainer height="500px">
            <DefaultLoader size={1.5} />
          </LoaderContainer>
        ) : (
            <Container>
              {' '}
              <TrainingRegistered />
            </Container>
          )}
      </div>
    );
  }

  if (
    user?.mentorStatus === 'ACCEPTED' || user?.mentorStatus === 'AVAILABLE' ||
    user?.mentorStatus === 'MENTORSHIP_ACTIVE'
  ) {
    return (
      <div>
        <Head />

        {!form || loading || !user ? (
          <LoaderContainer height="500px">
            <DefaultLoader size={1.5} />
          </LoaderContainer>
        ) : (
            <Container>
              <p>
                Vous pouvez vous inscrire à nouveau à la formation à l'aide du
                calendrier ci-dessous:
            </p>
              <InlineWidget
                url="https://calendly.com/mentorat-2/formation-mentors?hide_gdpr_banner=1"
                prefill={{
                  email: user.email,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  customAnswers: {
                    a1: user.phone || '',
                  },
                }}
              />
            </Container>
          )}
      </div>
    );
  }

  return (
    <div>
      <Head />

      {!form || loading || !user ? (
        <LoaderContainer height="500px">
          <DefaultLoader size={1.5} />
        </LoaderContainer>
      ) : (
          <Container>
            <p>Votre statut actuel ne vous permet pas de passer la formation.</p>
          </Container>
        )}
    </div>
  );
};

const TrainingRegistered: FC = () => {
  const { data } = useSWR<{
    eventFinishDate: string;
    eventStartDate: string;
    trainingFacultative: boolean;
  }>('/calendly/training-info');
  return (
    <div>
      <div style={{ display: 'grid', placeItems: 'center' }}>
        <FeaturedIcon icon={<CheckIcon />} />
      </div>
      <p>
        Vous avez réservé un créneau pour la <b>formation standard</b>
        {data?.eventStartDate
          ? ` à la date du ${getLocaleDate(data.eventStartDate)} à ${
          data?.eventStartDate && getLocaleTime(data.eventStartDate)
          }.`
          : '.'}
      </p>
      <p>
        Vous pouvez annuler ou replanifier votre rendez-vous depuis l'email qui
        vous a été envoyé.
      </p>
    </div>
  );
};

export default Training;
